import { titleItems, type GameSnapshot } from '../engine/casino';
import { money } from '../format';
import { GAMES } from '../engine/games';
import { unfinishedGame, type GameActions } from '../useGame';

/** "Back to Blackjack ($1,250)" when a play was left unfinished, else "Continue ($1,250)". */
function continueLabel(snapshot: GameSnapshot): string {
  const run = snapshot.run!;
  const open = unfinishedGame(run);
  return open ? `Back to ${GAMES[open].name} (${money(run.bankroll)})` : `Continue (${money(run.bankroll)})`;
}

export function Title({ snapshot, actions }: { snapshot: GameSnapshot; actions: GameActions }) {
  if (snapshot.screen === 'resetConfirm' || snapshot.screen === 'newRunConfirm') {
    const reset = snapshot.screen === 'resetConfirm';
    return (
      <div className="center-screen">
        <div className="block paper dialog">
          <h2>{reset ? 'Reset all progress?' : 'Start a new run?'}</h2>
          <p>{reset ? 'Every Gift Shop upgrade and record is wiped. This can’t be undone.' : 'The run you have going ends here, and counts as finished.'}</p>
          <div className="dialog-buttons">
            <button className="btn btn-secondary" onClick={() => actions.confirm(false)}>Keep playing</button>
            <button className="btn btn-danger" onClick={() => actions.confirm(true)}>{reset ? 'Reset' : 'New run'}</button>
          </div>
        </div>
      </div>
    );
  }
  const meta = snapshot.meta;
  return (
    <div className="center-screen">
      <div className="block title-block big">
        <h1>Feelin' Lucky</h1>
      </div>
      <div className="title-menu">
        {titleItems(snapshot).map((item) => (
          <button
            key={item.id}
            className={`btn ${item.id === 'reset' ? 'btn-quiet' : item.id === 'continue' || !snapshot.run ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => actions.titleItem(item.id)}
          >
            {item.id === 'continue' && snapshot.run ? continueLabel(snapshot) : item.label}
          </button>
        ))}
      </div>
      {meta.runsPlayed > 0 && (
        <p className="records">
          Runs {meta.runsPlayed} · Best {money(meta.bestPeakBankroll)} · Highest floor {meta.highestFloor}
          {meta.casinosBought ? ` · Casinos bought ${meta.casinosBought}` : ''}
        </p>
      )}
    </div>
  );
}

export function Bust({ snapshot, actions }: { snapshot: GameSnapshot; actions: GameActions }) {
  const stats = snapshot.lastRun;
  const how = snapshot.lastRunEnd;
  const heading = how === 'won' ? 'You bought the casino!' : how === 'blackout' ? 'Blackout.' : 'Busted.';
  const sub =
    how === 'won'
      ? `You finished with ${money(snapshot.lastRunCash)}.`
      : how === 'blackout'
        ? 'You wake up outside the casino with your pockets empty...'
        : "You can't cover the cheapest bet on this floor.";
  return (
    <div className="center-screen">
      <div className="block paper dialog">
        <h2>{heading}</h2>
        <p>{sub}</p>
        {stats && (
          <dl className="stats">
            <dt>Plays</dt><dd>{stats.plays}</dd>
            <dt>Peak bankroll</dt><dd>{money(stats.peakBankroll)}</dd>
            <dt>Biggest win</dt><dd>{money(Math.max(0, stats.biggestWin))}</dd>
            <dt>Drinks</dt><dd>{stats.drinksOrdered}</dd>
            {stats.jackpotsWon > 0 && (<><dt>Jackpots</dt><dd>{stats.jackpotsWon}</dd></>)}
          </dl>
        )}
        <button className="btn btn-primary" onClick={actions.dismiss}>Back to the door</button>
      </div>
    </div>
  );
}

/** Screens the web game hasn't built yet (the Bar, Gift Shop, elevator...). */
export function NotBuilt({ actions }: { actions: GameActions }) {
  return (
    <div className="center-screen">
      <div className="block paper dialog">
        <h2>Coming soon</h2>
        <p>This part of the casino isn’t open in the web version yet.</p>
        <button className="btn btn-primary" onClick={actions.back}>Back</button>
      </div>
    </div>
  );
}
